class WProductBuyer extends HTMLElement {
  connectedCallback() {
    this.controller = new AbortController();
    const { signal } = this.controller;

    const decoder = document.createElement('textarea');
    decoder.innerHTML = this.dataset.moneyFormat || '{{amount}}€';
    this.moneyFormat = decoder.value;

    this.form = this.querySelector('.w-pb-form');
    this.variantInput = this.querySelector('[data-variant-input]');
    this.planInput = this.querySelector('[data-plan-input]');
    this.qtyInput = this.querySelector('[data-qty-input]');
    this.qtySelect = this.querySelector('.w-pb-qty__select');
    this.tiers = Array.from(this.querySelectorAll('.w-pb-tier'));
    this.freqOptions = Array.from(this.querySelectorAll('.w-pb-freq__option'));
    this.activeTier = this.tiers.find((t) => t.classList.contains('is-active')) || this.tiers[0];
    this.activeFreq = this.freqOptions.find((f) => f.classList.contains('is-active')) || this.freqOptions[0];

    this.tiers.forEach((tier) => tier.addEventListener('click', () => this.selectTier(tier), { signal }));
    this.freqOptions.forEach((opt) => {
      opt.addEventListener('click', () => this.selectFreq(opt), { signal });
      opt.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          this.selectFreq(opt);
        }
      }, { signal });
    });

    this.qtySelect?.addEventListener('change', () => this.render(), { signal });

    this.initGallery(signal);
    this.initFaq(signal);
    this.initDelivery();

    this.form?.addEventListener('submit', (event) => this.submit(event), { signal });

    this.render();
  }

  disconnectedCallback() {
    this.controller?.abort();
  }

  formatMoney(cents) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    let value = '';
    const placeholder = /\{\{\s*(\w+)\s*\}\}/;
    const number = Number(cents);
    if (Number.isNaN(number)) return '';
    const withComma = (num, precision = 2, thousands = '.', decimal = ',') => {
      const fixed = (num / 100).toFixed(precision);
      const parts = fixed.split('.');
      const int = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return precision ? int + decimal + parts[1] : int;
    };
    switch ((this.moneyFormat.match(placeholder) || [])[1]) {
      case 'amount': value = withComma(number, 2, ',', '.'); break;
      case 'amount_no_decimals': value = withComma(number, 0, ',', '.'); break;
      case 'amount_with_comma_separator': value = withComma(number, 2, '.', ','); break;
      case 'amount_with_space_separator': value = withComma(number, 2, ' ', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = withComma(number, 0, '.', ','); break;
      case 'amount_no_decimals_with_space_separator': value = withComma(number, 0, ' ', ''); break;
      default: value = withComma(number, 2, '.', ',');
    }
    return this.moneyFormat.replace(placeholder, value);
  }

  stripZeroCents(text) {
    return text.replace(/([.,])00(\D|$)/, '$2');
  }

  price(el, cents) {
    if (el) el.textContent = this.stripZeroCents(this.formatMoney(cents));
  }

  selectTier(tier) {
    this.tiers.forEach((t) => t.classList.toggle('is-active', t === tier));
    this.activeTier = tier;
    this.render();
  }

  selectFreq(opt) {
    this.freqOptions.forEach((f) => f.classList.toggle('is-active', f === opt));
    this.activeFreq = opt;
    this.render();
  }

  q(freq, key) {
    return this.querySelector(`[data-freq="${freq}"] [data-price-${key}]`);
  }

  perDay(cents, days) {
    return this.stripZeroCents(this.formatMoney(Math.round(cents / days))) + ' /jour';
  }

  render() {
    const tier = this.activeTier;
    if (!tier) return;

    const qty = this.qtySelect ? parseInt(this.qtySelect.value, 10) || 1 : 1;
    const addQty = parseInt(tier.dataset.addQty, 10) || 1;
    const months = parseInt(tier.dataset.months, 10) || 1;
    const priceOnce = parseInt(tier.dataset.price, 10) || 0;
    const priceSub = tier.dataset.priceSub ? parseInt(tier.dataset.priceSub, 10) : null;
    const isSub = this.activeFreq && this.activeFreq.dataset.freq === 'subscription';
    const days = 30 * months;

    if (this.variantInput) this.variantInput.value = tier.dataset.variantId || '';
    if (this.qtyInput) this.qtyInput.value = addQty * qty;
    if (this.planInput) this.planInput.value = isSub && priceSub != null ? (tier.dataset.sellingPlanId || '') : '';

    const noteEl = this.querySelector('[data-step-note]');
    if (noteEl && tier.dataset.note) noteEl.textContent = tier.dataset.note;

    this.price(this.q('onetime', 'value'), priceOnce);
    const onceDay = this.q('onetime', 'day');
    if (onceDay) onceDay.textContent = this.perDay(priceOnce, days);

    const subValue = this.q('subscription', 'value');
    if (subValue) {
      const subCompare = this.q('subscription', 'compare');
      const subDay = this.q('subscription', 'day');
      const subSave = this.q('subscription', 'save');
      if (priceSub != null) {
        const saved = priceOnce - priceSub;
        if (subCompare) subCompare.textContent = saved > 0 ? this.stripZeroCents(this.formatMoney(priceOnce)) : '';
        this.price(subValue, priceSub);
        if (subSave) subSave.textContent = saved > 0 ? this.stripZeroCents(this.formatMoney(saved)) : '';
        if (subDay) subDay.textContent = this.perDay(priceSub, days);
      } else {
        if (subCompare) subCompare.textContent = '';
        if (subSave) subSave.textContent = '';
        this.price(subValue, priceOnce);
        if (subDay) subDay.textContent = this.perDay(priceOnce, days);
      }
    }

    this.tiers.forEach((t) => {
      const priceEl = t.querySelector('.w-pb-tier__price');
      if (!priceEl) return;
      const tMonths = parseInt(t.dataset.months, 10) || 1;
      const tOnce = parseInt(t.dataset.price, 10) || 0;
      const tSub = t.dataset.priceSub ? parseInt(t.dataset.priceSub, 10) : null;
      const perSachet = Math.round((isSub && tSub != null ? tSub : tOnce) / tMonths);
      priceEl.textContent = this.stripZeroCents(this.formatMoney(perSachet)) + ' /sachet';
    });

    const total = (isSub && priceSub != null ? priceSub : priceOnce) * qty;
    this.price(this.querySelector('[data-cta-price]'), total);
  }

  initGallery(signal) {
    this.slidesEl = this.querySelector('.w-pb-gallery__slides');
    this.slides = Array.from(this.querySelectorAll('.w-pb-gallery__slide'));
    this.thumbs = Array.from(this.querySelectorAll('.w-pb-gallery__thumb'));
    this.galleryIndex = 0;
    this.galleryCount = this.slides.length;
    if (this.galleryCount <= 1) return;

    this.prevBtn = this.querySelector('.w-pb-gallery__arrow--prev');
    this.nextBtn = this.querySelector('.w-pb-gallery__arrow--next');
    this.prevBtn?.addEventListener('click', () => this.goTo(this.galleryIndex - 1), { signal });
    this.nextBtn?.addEventListener('click', () => this.goTo(this.galleryIndex + 1), { signal });
    this.thumbs.forEach((thumb, i) => thumb.addEventListener('click', () => this.goTo(i), { signal }));

    let startX = null;
    const viewport = this.querySelector('.w-pb-gallery__main');
    viewport?.addEventListener('touchstart', (e) => { startX = e.touches[0].clientX; }, { passive: true, signal });
    viewport?.addEventListener('touchend', (e) => {
      if (startX === null) return;
      const delta = e.changedTouches[0].clientX - startX;
      startX = null;
      if (Math.abs(delta) > 40) this.goTo(this.galleryIndex + (delta < 0 ? 1 : -1));
    }, { passive: true, signal });

    this.updateGallery();
  }

  goTo(index) {
    this.galleryIndex = Math.max(0, Math.min(index, this.galleryCount - 1));
    this.updateGallery();
  }

  updateGallery() {
    if (this.slidesEl) this.slidesEl.style.transform = `translateX(${-this.galleryIndex * 100}%)`;
    this.thumbs.forEach((t, i) => t.classList.toggle('is-active', i === this.galleryIndex));
    if (this.prevBtn) this.prevBtn.disabled = this.galleryIndex <= 0;
    if (this.nextBtn) this.nextBtn.disabled = this.galleryIndex >= this.galleryCount - 1;
  }

  initFaq(signal) {
    const list = this.querySelector('.w-pb-faq');
    list?.addEventListener('click', (event) => {
      const trigger = event.target.closest('.w-pb-faq__q');
      if (!trigger || !list.contains(trigger)) return;
      const open = trigger.getAttribute('aria-expanded') !== 'true';
      trigger.setAttribute('aria-expanded', String(open));
      trigger.closest('.w-pb-faq__item')?.classList.toggle('is-open', open);
    }, { signal });
  }

  initDelivery() {
    const el = this.querySelector('[data-delivery]');
    if (!el || this.dataset.deliveryDynamic !== 'true') return;
    const min = parseInt(this.dataset.deliveryMin, 10) || 2;
    const max = parseInt(this.dataset.deliveryMax, 10) || 4;
    const prefix = el.dataset.deliveryPrefix || 'Livré entre :';
    const countSaturday = this.dataset.deliverySaturday !== 'false';
    const fmt = new Intl.DateTimeFormat('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
    const addBusinessDays = (n) => {
      const d = new Date();
      let added = 0;
      while (added < n) {
        d.setDate(d.getDate() + 1);
        const day = d.getDay();
        if (day === 0) continue;
        if (day === 6 && !countSaturday) continue;
        added += 1;
      }
      return d;
    };
    el.textContent = `${prefix} ${fmt.format(addBusinessDays(min))} et ${fmt.format(addBusinessDays(max))}`;
  }

  async submit(event) {
    if (!this.form) return;
    event.preventDefault();

    const button = this.querySelector('.w-pb-submit');
    const variantId = this.variantInput?.value;
    if (!variantId) return;

    const qty = this.qtySelect ? parseInt(this.qtySelect.value, 10) || 1 : 1;
    const addQty = parseInt(this.activeTier?.dataset.addQty, 10) || 1;
    const lineQty = addQty * qty;
    const plan = this.planInput?.value;
    const isSub = this.activeFreq && this.activeFreq.dataset.freq === 'subscription';

    const items = [{ id: Number(variantId), quantity: lineQty }];
    if (plan) items[0].selling_plan = Number(plan);

    const giftId = this.dataset.freeVariantId;
    const giftMode = this.dataset.giftMode || 'always';
    // La quantité de gourdes offertes suit le sélecteur "qty" (nombre de personnes/foyers),
    // pas "lineQty" (qui inclut aussi le nombre de mois du palier choisi).
    if (giftId && (giftMode === 'always' || (giftMode === 'subscription' && isSub))) {
      items.push({ id: Number(giftId), quantity: qty });
    }

    button?.setAttribute('aria-busy', 'true');
    const root = window.Shopify?.routes?.root || '/';
    const headers = { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' };

    try {
      await fetch(`${root}cart/clear.js`, { method: 'POST', headers });
      const res = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ items }),
      });
      if (!res.ok) throw new Error('add-to-cart failed');
      window.location.href = this.dataset.cartRedirect || `${root}checkout`;
    } catch (error) {
      button?.removeAttribute('aria-busy');
      this.form.submit();
    }
  }
}

customElements.define('w-product-buyer', WProductBuyer);